/**
 * Performance utilities for measuring and tracking application performance.
 * This module provides a wrapper around the browser's Performance API with
 * fallbacks for environments where the API is not available.
 */

/**
 * Interface for a performance measure when using the fallback implementation
 */
interface CustomPerformanceMeasure {
  name: string;
  entryType: string;
  startTime: number;
  duration: number;
  detail: any;
  toJSON(): any;
}

/**
 * A wrapper around the browser's Performance API that provides fallbacks
 * for environments where the API is not available.
 */
class PerformanceUtil {
  private marks: Record<string, number> = {};
  private measures: Record<string, CustomPerformanceMeasure> = {};
  private isPerformanceSupported: boolean;

  constructor() {
    // Check if the Performance API is supported
    this.isPerformanceSupported =
      typeof window !== 'undefined' && typeof window.performance !== 'undefined' && typeof window.performance.mark === 'function';
  }

  /**
   * Creates a timestamp mark in the browser's performance entry buffer with the given name.
   * Falls back to storing the current timestamp if the Performance API is not available.
   *
   * @param markName - The name to give the mark
   */
  mark(markName: string): void {
    if (this.isPerformanceSupported) {
      window.performance.mark(markName);
    } else {
      this.marks[markName] = Date.now();
    }
  }

  /**
   * Creates a named performance measurement between two marks.
   * Falls back to calculating the difference between stored timestamps if the Performance API is not available.
   *
   * @param measureName - The name to give the measure
   * @param startMark - The name of the starting mark
   * @param endMark - The name of the ending mark
   */
  measure(measureName: string, startMark: string, endMark: string): void {
    if (this.isPerformanceSupported) {
      try {
        window.performance.measure(measureName, startMark, endMark);
      } catch (e) {
        console.warn(`Failed to create performance measure ${measureName}:`, e);
      }
    } else if (this.marks[startMark] && this.marks[endMark]) {
      const duration = this.marks[endMark] - this.marks[startMark];
      this.measures[measureName] = {
        name: measureName,
        entryType: 'measure',
        startTime: this.marks[startMark],
        duration,
        detail: null,
        toJSON: function () {
          return {
            name: this.name,
            entryType: this.entryType,
            startTime: this.startTime,
            duration: this.duration
          };
        }
      };
    }
  }

  /**
   * Returns a list of PerformanceEntry objects based on the specified filter.
   * Falls back to returning stored measures if the Performance API is not available.
   *
   * @param name - The name of the entries to retrieve
   * @returns An array of PerformanceEntry objects
   */
  getEntriesByName(name: string): PerformanceEntry[] {
    if (this.isPerformanceSupported) {
      return window.performance.getEntriesByName(name);
    } else if (this.measures[name]) {
      return [this.measures[name] as unknown as PerformanceEntry];
    }
    return [];
  }

  /**
   * Clears all performance marks and measures.
   * Falls back to clearing stored marks and measures if the Performance API is not available.
   */
  clearMarks(): void {
    if (this.isPerformanceSupported) {
      window.performance.clearMarks();
    } else {
      this.marks = {};
    }
  }

  /**
   * Clears all performance measures.
   * Falls back to clearing stored measures if the Performance API is not available.
   */
  clearMeasures(): void {
    if (this.isPerformanceSupported) {
      window.performance.clearMeasures();
    } else {
      this.measures = {};
    }
  }
}

// Export a singleton instance of the PerformanceUtil class
export const performance = new PerformanceUtil();
